// Référencement : une seule source pour le titre, la description, l'URL
// canonique, les balises de partage et les données structurées de chaque
// page. Lue par le navigateur (useSeo) et par le pré-rendu
// (scripts/prerender.js), pour que le HTML servi et la page affichée
// disent la même chose.
import {
  companyInfo,
  services,
  formations,
  serviceParSlug,
  formationParSlug,
  faq,
  vedettes,
} from "./data/mockData";

export const SITE_URL = "https://geodonnee.com";
const IMAGE = `${SITE_URL}/og-geodonnee.png`;
const ORG_ID = `${SITE_URL}/#organisation`;

const { contact } = companyInfo;

const organisation = {
  "@type": "ProfessionalService",
  "@id": ORG_ID,
  name: "Geodonnée",
  url: `${SITE_URL}/`,
  logo: `${SITE_URL}/logo.png`,
  image: IMAGE,
  description:
    "Traitement, automatisation et diffusion de données géospatiales, et formation d'équipes techniques à QGIS, FME, PostGIS et Python.",
  email: contact.email,
  address: {
    "@type": "PostalAddress",
    streetAddress: contact.street,
    addressLocality: contact.city,
    addressRegion: contact.region,
    postalCode: contact.postalCode,
    addressCountry: "CA",
  },
  openingHoursSpecification: {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    opens: "09:00",
    closes: "17:00",
  },
  areaServed: [
    { "@type": "City", name: "Gatineau" },
    { "@type": "City", name: "Ottawa" },
    { "@type": "AdministrativeArea", name: "Québec" },
    { "@type": "Country", name: "Canada" },
  ],
  knowsAbout: ["Géomatique", "SIG", "QGIS", "FME", "PostGIS", "Python", "GeoServer", "Cartographie web", "Développement logiciel"],
};

const filAriane = (items) => ({
  "@type": "BreadcrumbList",
  itemListElement: items.map(([name, path], i) => ({
    "@type": "ListItem",
    position: i + 1,
    name,
    item: `${SITE_URL}${path}`,
  })),
});

/** Toutes les pages à pré-rendre et à déclarer dans le sitemap. */
export const routesIndexables = () => [
  "/",
  "/services",
  ...services.map((s) => `/services/${s.slug}`),
  "/formations",
  ...formations.map((f) => `/formations/${f.slug}`),
];

const introuvable = {
  title: "Page introuvable · Geodonnée",
  description: "Cette page n'existe pas ou a été déplacée.",
  path: null,
  robots: "noindex",
  jsonLd: [],
};

export const getSeo = (pathname) => {
  const path = pathname.replace(/\/+$/, "") || "/";

  if (path === "/") {
    return {
      title: "Geodonnée · Géomatique et automatisation SIG à Gatineau",
      description:
        "Géomatique à Gatineau : automatisation SIG, cartes web, développement sur mesure, LizPack Field et LizPack Viewer, formations QGIS, FME et PostGIS.",
      path: "/",
      jsonLd: [
        organisation,
        {
          "@type": "WebSite",
          "@id": `${SITE_URL}/#site`,
          url: `${SITE_URL}/`,
          name: "Geodonnée",
          inLanguage: "fr-CA",
          publisher: { "@id": ORG_ID },
        },
        ...vedettes.map((v) => ({
          "@type": "SoftwareApplication",
          name: v.nom,
          description: v.texte,
          url: v.lien,
          applicationCategory: "BusinessApplication",
          operatingSystem: v.systeme,
          publisher: { "@id": ORG_ID },
        })),
        {
          "@type": "FAQPage",
          mainEntity: faq.map(({ q, r }) => ({
            "@type": "Question",
            name: q,
            acceptedAnswer: { "@type": "Answer", text: r },
          })),
        },
      ],
    };
  }

  const [, section, slug] = path.split("/");

  const liste = (elements, chemin) => ({
    "@type": "ItemList",
    itemListElement: elements.map((e, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: e.title,
      url: `${SITE_URL}/${chemin}/${e.slug}`,
    })),
  });

  if (path === "/services") {
    return {
      title: "Services de géomatique à Gatineau · Geodonnée",
      description:
        "Traitement et automatisation SIG, diffusion, cartes web et développement sur mesure, sous-traitance de saisie et de collecte, formation. Gatineau et Québec.",
      path,
      jsonLd: [organisation, liste(services, "services"), filAriane([["Accueil", "/"], ["Services", path]])],
    };
  }

  if (path === "/formations") {
    return {
      title: "Formations QGIS, FME, PostGIS et Python · Geodonnée",
      description:
        "Formations en géomatique à Gatineau ou à distance : QGIS, FME, PostgreSQL/PostGIS, Python et cartographie web, en petits groupes.",
      path,
      jsonLd: [organisation, liste(formations, "formations"), filAriane([["Accueil", "/"], ["Formations", path]])],
    };
  }

  if (section === "services") {
    const service = serviceParSlug(slug);
    if (!service) return introuvable;
    return {
      title: `${service.title} à Gatineau · Geodonnée`,
      description: service.description,
      path,
      jsonLd: [
        organisation,
        {
          "@type": "Service",
          name: service.title,
          description: service.intro,
          url: `${SITE_URL}${path}`,
          serviceType: service.title,
          provider: { "@id": ORG_ID },
          areaServed: organisation.areaServed,
        },
        filAriane([
          ["Accueil", "/"],
          ["Services", "/services"],
          [service.title, path],
        ]),
      ],
    };
  }

  if (section === "formations") {
    const formation = formationParSlug(slug);
    if (!formation) return introuvable;
    const instance = {
      "@type": "CourseInstance",
      courseMode: formation.modes,
      inLanguage: "fr-CA",
      ...(formation.heures && { courseWorkload: `PT${formation.heures}H` }),
    };
    return {
      title: `Formation ${formation.title} · Geodonnée`,
      description: `${formation.description} ${formation.duration}, ${formation.format.toLowerCase()}.`,
      path,
      jsonLd: [
        organisation,
        {
          "@type": "Course",
          name: formation.title,
          description: formation.description,
          url: `${SITE_URL}${path}`,
          inLanguage: "fr-CA",
          educationalLevel: formation.level,
          coursePrerequisites: formation.prerequisites,
          teaches: formation.topics,
          provider: { "@id": ORG_ID },
          hasCourseInstance: [instance],
        },
        filAriane([
          ["Accueil", "/"],
          ["Formations", "/formations"],
          [formation.title, path],
        ]),
      ],
    };
  }

  return introuvable;
};

/** Balises de l'en-tête pour une page, communes au navigateur et au pré-rendu. */
export const seoTags = (seo) => {
  const url = seo.path ? `${SITE_URL}${seo.path}` : null;
  const metas = [
    { name: "description", content: seo.description },
    { name: "robots", content: seo.robots || "index, follow" },
    { property: "og:type", content: "website" },
    { property: "og:site_name", content: "Geodonnée" },
    { property: "og:locale", content: "fr_CA" },
    { property: "og:title", content: seo.title },
    { property: "og:description", content: seo.description },
    { property: "og:image", content: IMAGE },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    {
      property: "og:image:alt",
      content: "Geodonnée, géomatique et automatisation à Gatineau",
    },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: seo.title },
    { name: "twitter:description", content: seo.description },
    { name: "twitter:image", content: IMAGE },
  ];
  if (url) metas.push({ property: "og:url", content: url });

  const jsonLd = seo.jsonLd.length
    ? JSON.stringify({ "@context": "https://schema.org", "@graph": seo.jsonLd })
    : null;

  return { title: seo.title, metas, canonical: url, jsonLd };
};
